import axios from '@/axios-client.js';
import router from '../../router';
import { objIsEmpty, findXformValue, activateFieldsNforceValues } from'@/utils';

const feature = {
  namespaced: true,
  state: {
    attachmentFormset: [],
    attachmentsToDelete: [],
    checkedFeatures: [],
    uncheckedFeatures: [],
    extra_forms: [],
    features: [],
    features_count: 0,
    type_counts: {},
    currentFeature: null,
    form: null,
    linkedFormset: [], // used to edit in feature_edit
    linked_features: [], // used to display in feature_detail
    allSelected: false, // used to select all in feature list & map
  },

  getters: {
    checkedFeaturesSet: state => new Set(state.checkedFeatures.map(el => el.feat_id)),
    uncheckedFeaturesSet: state => new Set(state.uncheckedFeatures.map(el => el.feat_id)),
    totalFeaturesCount: state => {
      return Object.values(state.type_counts).reduce((sum, count) => sum + count, 0);
    },
    hasSelection: (state, getters) => {
      if (state.allSelected) {
        // In "all selected" mode, there is a selection if uncheckedFeatures is not empty
        // or if allSelected is true (since everything is selected except exclusions)
        return state.uncheckedFeatures.length < getters.totalFeaturesCount;
      } else {
        // In manual mode, there is a selection if checkedFeatures is not empty
        return state.checkedFeatures.length > 0;
      }
    },
    selectedFeaturesCount: (state, getters) => {
      if (state.allSelected) {
        // In "all selected" mode, the number of selected features is the total minus exclusions
        return getters.totalFeaturesCount - state.uncheckedFeatures.length;
      } else {
        // In manual mode, the number of selected features is simply the size of checkedFeatures
        return state.checkedFeatures.length;
      }
    },
    selectedTypeCounts: state => {
      if (state.allSelected) {
        // "All selected" case: start from total counts and subtract exclusions
        const selectedCounts = { ...state.type_counts };
        // Subtract uncheckedFeatures
        state.uncheckedFeatures.forEach(({ ft_slug }) => {
          if (selectedCounts[ft_slug] !== undefined) {
            selectedCounts[ft_slug] -= 1;
            if (selectedCounts[ft_slug] <= 0) {
              delete selectedCounts[ft_slug];
            }
          }
        });
        return selectedCounts;
      } else {
        // Manual selection case: count occurrences of each type in checkedFeatures
        const selectedCounts = {};
        state.checkedFeatures.forEach(({ ft_slug }) => {
          selectedCounts[ft_slug] = (selectedCounts[ft_slug] || 0) + 1;
        });
        return selectedCounts;
      }
    },
    massSelectionPayload: (state, getters) => {
      const requestData = { feature_ids: [], exclude: false };
      if (!state.allSelected && getters.checkedFeaturesSet.size > 0) {
        requestData.feature_ids = Array.from(getters.checkedFeaturesSet);
        requestData.exclude = false;
      } else if (state.allSelected && getters.uncheckedFeaturesSet.size > 0) {
        requestData.feature_ids = Array.from(getters.uncheckedFeaturesSet);
        requestData.exclude = true;
      }
      return requestData;
    },
  },

  mutations: {
    SET_FEATURES(state, features) {
      state.features = features.sort((a, b) => {
        return new Date(b.created_on) - new Date(a.created_on); // sort features chronologically
      });
    },
    SET_FEATURES_COUNT(state, features_count) {
      state.features_count = features_count;
    },
    SET_TYPE_COUNTS(state, type_counts) {
      state.type_counts = type_counts;
    },
    SET_CURRENT_FEATURE(state, feature) {
      state.currentFeature = feature;
    },
    UPDATE_FORM(state, payload) {
      state.form = payload;
    },
    INIT_FORM(state) {
      state.form = {
        title: state.currentFeature.properties.title,
        description: { value: state.currentFeature.properties.description },
        status: { value: state.currentFeature.properties.status },
        assigned_member: { value: state.currentFeature.properties.assigned_member },
      };
    },
    UPDATE_FORM_FIELD(state, field) {
      if (state.form[field.name].value !== undefined) {
        state.form[field.name].value = field.value;
      } else {
        state.form[field.name] = field.value;
      }
    },
    UPDATE_EXTRA_FORM(state, extra_form) {
      const updatedExtraForms = state.extra_forms.map((field) => field.name === extra_form.name ? extra_form : field);
      state.extra_forms = activateFieldsNforceValues(updatedExtraForms);
    },
    SET_EXTRA_FORMS(state, extra_forms) {
      state.extra_forms = extra_forms;
    },
    CLEAR_EXTRA_FORM(state) {
      state.extra_forms = [];
    },
    ADD_ATTACHMENT_FORM(state, attachmentFormset) {
      state.attachmentFormset = [...state.attachmentFormset, attachmentFormset];
    },
    UPDATE_ATTACHMENT_FORM(state, payload) {
      const index = state.attachmentFormset.findIndex((el) => el.dataKey === payload.dataKey);
      if (index !== -1) {
        state.attachmentFormset[index] = payload;
      }
    },
    REMOVE_ATTACHMENT_FORM(state, payload) {
      state.attachmentFormset = state.attachmentFormset.filter(form => form.dataKey !== payload);
    },
    CLEAR_ATTACHMENT_FORM(state) {
      state.attachmentFormset = [];
    },
    ADD_LINKED_FORM(state, linkedFormset) {
      state.linkedFormset = [...state.linkedFormset, linkedFormset];
    },
    UPDATE_LINKED_FORM(state, payload) {
      const index = state.linkedFormset.findIndex((el) => el.dataKey === payload.dataKey);
      if (index !== -1) {
        state.linkedFormset[index] = payload;
      }
    },
    REMOVE_LINKED_FORM(state, payload) {
      state.linkedFormset = state.linkedFormset.filter(form => form.dataKey !== payload);
    },
    SET_LINKED_FEATURES(state, payload) {
      state.linked_features = payload;
    },
    CLEAR_LINKED_FORM(state) {
      state.linkedFormset = [];
    },
    ADD_ATTACHMENT_TO_DELETE(state, attachementId) {
      state.attachmentsToDelete.push(attachementId);
    },
    SET_ATTACHMENT_TO_DELETE(state, attachements) {
      state.attachmentsToDelete = attachements;
    },
    REMOVE_ATTACHMENTS_ID_TO_DELETE(state, attachementId) {
      state.attachmentsToDelete = state.attachmentsToDelete.filter(el => el !== attachementId);
    },
    SET_CHECKED_FEATURES(state, checkedFeatures) {
      state.checkedFeatures = checkedFeatures;
    },
    SET_UNCHECKED_FEATURES(state, uncheckedFeatures) {
      state.uncheckedFeatures = uncheckedFeatures;
    },
    SET_ALL_SELECTED(state, checked) {
      state.allSelected = checked;
    },
  },

  actions: {
    async GET_PROJECT_FEATURES({ commit, dispatch, rootState }, {
      project_slug,
      feature_type__slug,
      ordering,
      search,
      limit,
      geojson = false
    }) {      
      dispatch('CANCEL_CURRENT_SEARCH_REQUEST', null, { root: true });
      const cancelToken = axios.CancelToken.source();
      commit('SET_CANCELLABLE_SEARCH_REQUEST', cancelToken, { root: true });
      commit('SET_FEATURES', []);
      commit('SET_FEATURES_COUNT', 0);
      let url = `${rootState.configuration.VUE_APP_DJANGO_API_BASE}v2/features/?project__slug=${project_slug}`;
      if (feature_type__slug) {
        url = url.concat('', `${url.includes('?') ? '&' : '?'}feature_type__slug=${feature_type__slug}`);
      }
      if (ordering) {
        url = url.concat('', `${url.includes('?') ? '&' : '?'}ordering=${ordering}`);
      }
      if (search) {
        url = url.concat('', `${url.includes('?') ? '&' : '?'}title__icontains=${search}`);
      }
      if (limit) {
        url = url.concat('', `${url.includes('?') ? '&' : '?'}limit=${limit}`);
      }
      if (geojson) {
        url = url.concat('', '&output=geojson');
      }

      try {
        const response = await axios.get(url, { cancelToken: cancelToken.token });
        if (response.status === 200 && response.data) {
          commit('SET_FEATURES', response.data.features);
          commit('SET_FEATURES_COUNT', response.data.count);
        }
        return response;
      } catch (error) {
        if (error.message) {
          console.error(error);
        }
        throw error; // 'throw' instead of 'return', in order to pass inside the 'catch' error instead of 'then', to avoid initiating map in another component after navigation
      }
    },
    GET_PROJECT_FEATURE({ commit, dispatch, rootState }, { project_slug, feature_id }) {
      dispatch('CANCEL_CURRENT_SEARCH_REQUEST', null, { root: true });      
      const cancelToken = axios.CancelToken.source();
      commit('SET_CANCELLABLE_SEARCH_REQUEST', cancelToken, { root: true });
      const url = `${rootState.configuration.VUE_APP_DJANGO_API_BASE}v2/features/${feature_id}/?project__slug=${project_slug}`;
      return axios
        .get(url, { cancelToken: cancelToken.token })
        .then((response) => {
          if (response.status === 200 && response.data) {
            commit('SET_CURRENT_FEATURE', response.data);
          }
          return response;
        })
        .catch((error) => {
          console.error('Error while getting feature for id = ', feature_id, error);
          throw error;
        });
    },

    /**
     * Handles the entire lifecycle of a feature submission, from sending data to handling additional forms
     * and managing redirections based on the operation performed (create or update).
     * @param {Object} context - Vuex action context, including state and dispatch functions.
     * @param {Object} payload - Contains parameters like routeName, query, and extraForms for form handling.
     */
    SEND_FEATURE({ state, rootState, rootGetters, commit, dispatch }, { routeName, query, extraForms }) {

      function redirect(featureName, response) {
        //  when modifying more than 2 features, exit this function (to avoid conflict with next feature call to GET_PROJECT_FEATURE)
        if (routeName === 'editer-attribut-signalement') return response;
        let newQuery = { ...query }; // create a copy of query from the route to avoid redundant navigation error since the router object would be modified
        // Display a success message in the UI.
        commit(
          'DISPLAY_MESSAGE',
          {
            comment: routeName === 'ajouter-signalement' ?
              'Le signalement a été crée' :
              `Le signalement ${featureName} a été mis à jour`,
            level: 'positive'
          },
          { root: true },
        );

        // Construct the query for navigation based on the current state and feature details.
        const slug_type_signal = rootState['feature-type'].currentFeatureTypeSlug;
        const project = rootState.projects.project;
        if (routeName === 'ajouter-signalement' && !query.ordering) {
          newQuery = {
            ordering: project.feature_browsing_default_sort,
            offset: 0,// if feature was just created, in both ordering it would be the first in project features list
          };
          if (project.feature_browsing_default_filter === 'feature_type_slug')  {
            newQuery['feature_type_slug'] = slug_type_signal;
          }
        }
        if (query && query.ordering === '-updated_on') { // if the list is ordered by update time
          newQuery.offset = 0;// it would be in first position (else, if ordered by creation, the position won't change anyway)
        }
        // in fast edition avoid redundant navigation if query didn't change
        if (routeName === 'details-signalement-filtre' && parseInt(query.offset) === parseInt(newQuery.offset)) {
          return 'reloadPage';
        }

        // Perform the actual route navigation if needed.
        const redirect_url = rootState.configuration.VUE_APP_FEATURE_CREATION_REDIRECT_URL;
        if (redirect_url) {
          window.location.href = redirect_url;
        } else if (!objIsEmpty(newQuery)) {
          router.push({
            name: 'details-signalement-filtre',
            params: { slug_type_signal },
            query: newQuery,
          });
        } else {
          router.push({
            name: 'details-signalement',
            params: { slug_type_signal },
          });
        }
        return response;
      }

      async function handleOtherForms(
        featureId,
        featureName,
        response,
        attachmentsSnapshot,
        attachmentsToDeleteSnapshot,
        linkedFeaturesSnapshot
      ) {
        await dispatch('SEND_ATTACHMENTS', {
          featureId,
          attachmentsSnapshot,
          attachmentsToDeleteSnapshot
        });
        await dispatch('PUT_LINKED_FEATURES', {
          featureId,
          linkedFeaturesSnapshot
        });
        return redirect(featureName, response);
      }

      /**
       * Prepares a GeoJSON object from the current state and extra forms provided in the payload.
       * @return {Object} - A GeoJSON object representing the feature with additional properties.
       */
      function createGeojson() { //* prepare feature data to send
        const extraFormObject = {}; //* prepare an object to be flatten in properties of geojson
        // use extraForms from argument if defined, overiding data from the store, in order to not use mutation (in case of multiple features)
        for (const field of extraForms || state.extra_forms) {
          // send extra form only if there is a value defined or if no value, if there was a value before, in order to avoid sending empty value when user didn't touch the extraform
          if (field.value !== null ||
              (state.currentFeature.properties && state.currentFeature.properties[field.name])) {
            extraFormObject[field.name] = field.value;
          }
        }
        let geojson = {
          id: state.form.feature_id || state.currentFeature.id,
          type: 'Feature',
          properties: {
            title: state.form.title,
            description: state.form.description.value,
            status: state.form.status.value.value || state.form.status.value,
            project: rootState.projects.project.slug,
            feature_type: rootState['feature-type'].currentFeatureTypeSlug,
            assigned_member: state.form.assigned_member.value,
            ...extraFormObject
          }
        };
        // if not in the case of a non geographical feature type, add geometry to geojson, else send without geometry
        if (rootGetters['feature-type/feature_type'].geom_type !== 'none') {
          geojson['geometry'] = state.form.geometry || state.form.geom ||
            state.currentFeature.geometry || state.currentFeature.properties.geom;
        }
        return geojson;
      }

      const geojson = createGeojson(); // Construct the GeoJSON from current state.
      let url = `${rootState.configuration.VUE_APP_DJANGO_API_BASE}v2/features/`;
      if (routeName !== 'ajouter-signalement') {
        url += `${geojson.id}/?
        feature_type__slug=${rootState['feature-type'].currentFeatureTypeSlug} 
        &project__slug=${rootState.projects.project.slug}`;
      }

      //* postOrPutFeature function from service featureAPI could be used here, but because configuration is in store,
      //* projectBase would need to be sent with each function which imply to modify all function from this service,
      //* which could create regression
      return axios({
        url,
        method: routeName === 'ajouter-signalement' ? 'POST' : 'PUT',
        data: geojson
      }).then((response) => {
        if ((response.status === 200 || response.status === 201) && response.data) {
          const featureId = response.data.id;
          const featureName = response.data.properties.title;
          // Handle additional forms if needed.
          if (state.attachmentFormset.length > 0 ||
              state.linkedFormset.length > 0 ||
              state.attachmentsToDelete.length > 0) {
            // Fix the state to prevent reinit before sending
            const attachmentsSnapshot = state.attachmentFormset.map(a => ({ ...a }));
            const attachmentsToDeleteSnapshot = state.attachmentsToDelete.map(a => ({ ...a }));
            const linkedFeaturesSnapshot = state.linkedFormset.map(l => ({ ...l }));
            return handleOtherForms(
              featureId,
              featureName,
              response,
              attachmentsSnapshot,
              attachmentsToDeleteSnapshot,
              linkedFeaturesSnapshot
            );
          } else {
            return redirect(featureName, response);
          }
        }
      }).catch((error) => {
        // If offline, store the edited feature in localeStorage to send them when back online
        if (error.message === 'Network Error' || !rootState.isOnline) {
          let arraysOffline = [];
          const localStorageArray = localStorage.getItem('geocontrib_offline');
          if (localStorageArray) {
            arraysOffline = JSON.parse(localStorageArray);
          }
          const updateMsg = {
            project: rootState.projects.project.slug,
            type: routeName === 'ajouter-signalement' ? 'post' : 'put',
            featureId: geojson.id,
            geojson: geojson
          };
          arraysOffline.push(updateMsg);
          localStorage.setItem('geocontrib_offline', JSON.stringify(arraysOffline));

          router.push({
            name: 'offline-signalement',
            params: {
              slug_type_signal: rootState['feature-type'].currentFeatureTypeSlug
            },
          });
          return Promise.reject({ type: 'offline', message: 'Enregistrement hors-ligne' });
        }
        else {
          console.error('Error while sending feature', error);
          const data = error.response?.data || {};
          let message = data.detail || error.message || 'Erreur lors de l’enregistrement du signalement';
          let type = 'backend';
          // Errors with geometry (map)
          if (data.geometry) {
            type = 'map';
            message = data.geometry[0];
          }

          commit('DISPLAY_MESSAGE', {
            comment: message,
            level: 'negative'
          }, { root: true });
          return Promise.reject({ type, message, data });
        }
      });
    },

    async SEND_ATTACHMENTS({ rootState, commit, dispatch }, {
      featureId,
      attachmentsSnapshot,
      attachmentsToDeleteSnapshot
    }) {
      const DJANGO_API_BASE = rootState.configuration.VUE_APP_DJANGO_API_BASE;

      function addFileToRequest(attachment, attchmtId) {
        if (!attachment.fileToImport) {
          throw new Error('Fichier manquant');
        }

        const formdata = new FormData();
        formdata.append('file', attachment.fileToImport, attachment.fileToImport.name);

        return axios.put(
          `${DJANGO_API_BASE}features/${featureId}/attachments/${attchmtId}/upload-file/`,
          formdata
        );
      }

      function putOrPostAttachement(attachment) {
        if (!attachment.title || !attachment.title.trim()) {
          throw new Error('Titre manquant');
        }

        const formdata = new FormData();
        formdata.append('title', attachment.title);
        formdata.append('info', attachment.info || '');
        formdata.append('is_key_document', attachment.is_key_document);

        let url = `${DJANGO_API_BASE}features/${featureId}/attachments/`;
        if (attachment.id) url += `${attachment.id}/`;

        return axios({
          url,
          method: attachment.id ? 'PUT' : 'POST',
          data: formdata
        }).then((response) => {
          if (attachment.fileToImport) {
            return addFileToRequest(attachment, response.data.id);
          }
          return response;
        });
      }

      function deleteAttachement(attachmentsId) {
        return dispatch('DELETE_ATTACHMENTS', {
          attachmentsId,
          featureId
        });
      }

      await Promise.all([
        ...attachmentsSnapshot.map(putOrPostAttachement),
        ...attachmentsToDeleteSnapshot.map(deleteAttachement)
      ]);

      commit('SET_ATTACHMENT_TO_DELETE', []);
    },

    DELETE_ATTACHMENTS({ rootState }, payload) {
      return axios.delete(`${rootState.configuration.VUE_APP_DJANGO_API_BASE}features/${payload.featureId}/attachments/${payload.attachmentsId}/`);
    },

    PUT_LINKED_FEATURES({ rootState }, { featureId, linkedFeaturesSnapshot }) {
      return axios.put(
        `${rootState.configuration.VUE_APP_DJANGO_API_BASE}features/${featureId}/feature-links/`,
        linkedFeaturesSnapshot
      );
    },

    DELETE_FEATURE({ rootState }, payload) {
      const { feature_id, noFeatureType } = payload;
      let url = `${rootState.configuration.VUE_APP_DJANGO_API_BASE}v2/features/${feature_id}/?` +
      `project__slug=${rootState.projects.project.slug}`;
      if (!noFeatureType) {
        url +=`&feature_type__slug=${rootState['feature-type'].currentFeatureTypeSlug}`;
      }
      return axios
        .delete(url)
        .then((response) => response)
        .catch(() => {
          return false;
        });
    },
    /**
     * Initializes extra forms based on the current feature type and its custom fields.
     * This function retrieves custom fields for the current feature type, assigns values to them based on the current feature's properties,
     * and commits them to the store to be displayed in the form.
     * 
     * @param {Object} context - The Vuex action context, including state, rootGetters, and commit function.
     */
    INIT_EXTRA_FORMS({ state, rootGetters, commit }) {
      const feature = state.currentFeature; // Current feature being edited or viewed.
      const featureType = rootGetters['feature-type/feature_type']; // Retrieves the feature type from root getters.
      const customFields = featureType.customfield_set; // Custom fields defined for the feature type.

      if (customFields) {
        commit('SET_EXTRA_FORMS',
          activateFieldsNforceValues( // A hypothetical function to activate fields and enforce their values.
            customFields.map((field) => {
              // Determines the initial value for the field
              let value = feature.properties ? feature.properties[field.name] : findXformValue(feature, field);
              // If the field is a boolean and the value is null, sets it to false
              if (field.field_type === 'boolean' && value === null) {
                value = false;
              }
              // Returns a new object with the updated value and the rest of the field's properties
              return { ...field, value };
            })
          ).sort((a, b) => a.position - b.position) // Sorts fields by their user-defined position.
        );
      }
    },
  },
};

export default feature;
